import React, { useState } from "react";
import {
    FaPassport,
    FaExclamationTriangle,
    FaFileAlt,
    FaClock,
    FaEnvelope,
    FaInfoCircle,
    FaUserFriends,
    FaChevronDown,
    FaExternalLinkAlt,
    FaShieldAlt,
    FaCheckCircle,
} from "react-icons/fa";
import styles from "./VisaHelpPage.module.css";
import { FaCheck, FaRegCopy } from "react-icons/fa6";

const VisaHelpPage = () => {

    const [copied, setCopied] = useState(false);
    const email = "ivec2027@gmail.com";
    const [openFaq, setOpenFaq] = useState(null);

    const handleCopy = () => {
        navigator.clipboard.writeText(email);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        {
            q: "1. Do I need a visa to attend IVEC 2027 in India?",
            a: "Yes, all foreign nationals entering India are required to possess a valid international travel document in the form of a national passport with a valid visa or e-Visa obtained from an Indian Mission/Post abroad.",
        },
        {
            q: "2. Which visa category should I apply for?",
            a: "Participants attending the conference technical sessions are required to apply for a Conference Visa (or e-Conference Visa). Accompanying persons not attending technical sessions should apply for a Tourist Visa.",
        },
        {
            q: "3. How do I apply for an Indian Visa?",
            a: "Visa applications must be submitted online through the official Government of India portal: https://indianvisaonline.gov.in/",
        },
        {
            q: "4. What documents are required for a Conference Visa?",
            a: "Required documents include: Official Invitation Letter from IVEC 2027, Clearance Letter from Ministry of External Affairs (MEA), MHA Clearance (if applicable), Valid Passport (6 months validity, 2 blank pages), and Passport-size photos.",
        },
        {
            q: "5. How can I request an invitation letter?",
            a: "Official invitation letters will be issued by the IVEC 2027 Secretariat only after your conference registration is completed and full payment has been received.",
        },
        {
            q: "6. How long does the visa process take?",
            a: "Processing typically takes approximately 14 days for most countries. However, for specific countries requiring additional clearances (such as Afghanistan, Iraq, Pakistan, Sudan, China), it can take up to 60 days.",
        },
        {
            q: "7. Can the conference secretariat help if my visa is denied?",
            a: "No. Visa approval remains solely at the discretion of the Indian Embassy or Consulate. The organizers cannot intervene in visa decisions or appeal rejections.",
        },
    ];

    return (
        <div className={styles.pageWrapper}>
            {/* Page Header */}
            <header className={styles.header}>
                <div className={styles.headerBadge}>Travel Guidance</div>
                <h1 className={styles.mainTitle}>
                    Visa Guidelines – <span>IVEC 2027</span>
                </h1>
                <p className={styles.subtitle}>
                    The information on this page is provided as general guidance for
                    participants traveling to India to attend <strong>IVEC 2027</strong> in
                    Bengaluru (15–18 March 2027). While the organizers strive to keep this
                    information updated, it should not be considered legal advice.
                    Participants are advised to verify visa requirements with the nearest{" "}
                    <strong>Indian Embassy or Consulate</strong> before making travel
                    arrangements.
                </p>
            </header>

            {/* Main Grid Container */}
            <main className={styles.container}>
                {/* Passport Form Banner */}
                <section className={`${styles.card} ${styles.actionCard}`}>
                    <div className={styles.cardHeader}>
                        <FaPassport className={styles.cardIcon} />
                        <h2>Important Action: Passport Information Submission</h2>
                    </div>
                    <p>
                        Dear Participants,
                        <br />
                        As per Government of India regulations, all international participants —
                        including speakers, committee members, volunteers, authors, and
                        delegates — are required to submit their passport details in advance for
                        necessary clearances.
                    </p>
                    <p>
                        We request you to complete the passport information form at the earliest
                        using the link below:
                    </p>

                    <a
                        href="#"
                        // target="_blank"
                        // rel="noopener noreferrer"
                        className={styles.submitBtn}
                    >
                        <FaPassport /> Passport Information Submission Form
                    </a>

                    <div className={styles.infoGroup}>
                        <div className={styles.attendeeSection}>
                            <h3 className={styles.attendeeTitle}>Who needs to fill the form?</h3>

                            <div className={styles.badgeGrid}>
                                <div className={styles.attendeeBadge}>
                                    <FaCheckCircle className={styles.badgeIcon} />
                                    <span>International Speakers</span>
                                </div>

                                <div className={styles.attendeeBadge}>
                                    <FaCheckCircle className={styles.badgeIcon} />
                                    <span>International Organizing Committee Members</span>
                                </div>

                                <div className={styles.attendeeBadge}>
                                    <FaCheckCircle className={styles.badgeIcon} />
                                    <span>International Volunteers</span>
                                </div>

                                <div className={styles.attendeeBadge}>
                                    <FaCheckCircle className={styles.badgeIcon} />
                                    <span>International Authors / Presenters</span>
                                </div>

                                <div className={styles.attendeeBadge}>
                                    <FaCheckCircle className={styles.badgeIcon} />
                                    <span>International Attendees / Delegates</span>
                                </div>

                                <div className={styles.attendeeBadge}>
                                    <FaCheckCircle className={styles.badgeIcon} />
                                    <span>Sponsors / Exhibitors</span>
                                </div>
                            </div>
                        </div>

                        <div className={styles.deadlineBadge}>
                            <strong>Deadline:</strong> At the discretion of the Committee;
                            however, the Secretariat requests this information at the earliest.
                        </div>

                        <div className={styles.noteBox}>
                            <strong>Please note:</strong>
                            <ul>
                                <li>Only foreign passport holders are required to submit details.</li>
                                <li>
                                    Indian nationals and OCI card holders are <strong>not</strong>{" "}
                                    required to submit passport details.
                                </li>
                            </ul>
                            <p className={styles.cooperationText}>
                                Your cooperation is vital to ensure <strong>smooth processing of government approvals</strong> for the conference.
                            </p>
                        </div>
                    </div>
                </section>

                {/* General Information */}
                <section className={styles.card}>
                    <div className={styles.cardHeader}>
                        <FaInfoCircle className={styles.cardIcon} />
                        <h2>General Information</h2>
                    </div>
                    <p>
                        All international participants attending <strong>IVEC 2027</strong> must
                        obtain the necessary visa and government clearances before traveling to
                        India.
                    </p>
                    <p>
                        According to Government of India regulations, international attendees
                        must obtain <strong>clearance from the Ministry of External Affairs (MEA)</strong> prior to the conference.
                    </p>
                    <p>
                        Participants who are <strong>not Indian citizens and do not hold an OCI card</strong> must submit their passport details to the conference secretariat to obtain the required permissions.
                    </p>

                    <p>This requirement applies to all international participants, including:</p>
                    <ul className={styles.bulletListInline}>
                        <li>Speakers</li>
                        <li>Delegates</li>
                        <li>Authors</li>
                        <li>Committee Members</li>
                        <li>Accompanying attendees</li>
                    </ul>

                    <div className={styles.highlightBox}>
                        <p><strong>Participants must provide:</strong></p>
                        <ul>
                            <li>
                                A <strong>clear colored copy of their passport</strong> showing the{" "}
                                <strong>passport number, issue date, and expiry date</strong>.
                            </li>
                        </ul>
                        <p><strong>Visitors entering India must have:</strong></p>
                        <ul>
                            <li>
                                A passport <strong>valid for at least 6 months</strong> from the date of entry, and
                            </li>
                            <li>
                                <strong>At least two blank pages</strong> in the passport.
                            </li>
                        </ul>
                        <p className={styles.visaTypeNotice}>
                            Participants attending the conference are required to apply for a <strong>Conference Visa</strong>.
                        </p>
                    </div>
                </section>

                {/* MEA / MHA Clearance */}
                <section className={styles.card}>
                    <div className={styles.cardHeader}>
                        <FaShieldAlt className={styles.cardIcon} />
                        <h2>MEA / MHA Clearance</h2>
                    </div>
                    <p>
                        All non-Indian passport holders must submit their passport details through the conference registration form so that the organizers can obtain clearance from the <strong>Ministry of External Affairs (MEA), Government of India</strong>.
                    </p>
                    <p>
                        Participants from the following countries require <strong>additional political clearance from the Ministry of Home Affairs (MHA)</strong>:
                    </p>
                    <div className={styles.countryBadgeGrid}>
                        <span>Afghanistan</span>
                        <span>Iraq</span>
                        <span>Pakistan</span>
                        <span>Sudan</span>
                        <span>China</span>
                        <span>Stateless persons</span>
                        <span>Foreign nationals of Pakistani origin</span>
                    </div>

                    <div className={styles.warningAlert}>
                        <div className={styles.warningTitle}>
                            <FaExclamationTriangle /> Important – Participants from Pakistan
                        </div>
                        <p>Participants traveling from Pakistan must note:</p>
                        <ul>
                            <li>Entry into India must be through Delhi airport if traveling by air.</li>
                            <li>Bengaluru is not permitted as a port of entry for Pakistani citizens.</li>
                            <li>Applicants holding dual passports must apply using their Pakistani passport.</li>
                        </ul>
                    </div>
                </section>

                {/* Visa Application Process & Tips Grid */}
                <div className={styles.twoColumnGrid}>
                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <FaFileAlt className={styles.cardIcon} />
                            <h2>Visa Application Process</h2>
                        </div>
                        <p>
                            Visa applications must be submitted online through the official <strong>Government of India portal</strong>:
                        </p>
                        {/* Official Visa Portal Link */}
                        <a
                            href="https://indianvisaonline.gov.in/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.externalLinkBtn}
                        >
                            <span>Apply via Official Indian Visa Portal</span>
                            <FaExternalLinkAlt className={styles.linkIcon} />
                        </a>

                        <p style={{ marginTop: "1rem" }}>
                            Nationals from more than <strong>170 countries</strong> may also be eligible to apply for an <strong>e-Visa</strong>.
                        </p>
                        <p>
                            Additionally, all international travelers to India are required to complete the <strong>Online Arrival Form</strong>:
                        </p>
                        {/* Online Arrival Form Link */}
                        <a
                            href="https://indianvisaonline.gov.in/earrival/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.externalLinkBtn}
                        >
                            <span>Complete Online Arrival Form</span>
                            <FaExternalLinkAlt className={styles.linkIcon} />
                        </a>
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <FaInfoCircle className={styles.cardIcon} />
                            <h2>Tips for Application</h2>
                        </div>
                        <ul className={styles.checkList}>
                            <li>Save your <strong>Temporary Reference Number</strong> during the application process.</li>
                            <li>If the website freezes or gives an error, you can resume your application later using this reference number.</li>
                            <li>Print <strong>two copies of the completed visa application form</strong> and sign them.</li>
                            <li>Keep a saved digital copy for future reference.</li>
                        </ul>
                    </section>
                </div>

                {/* Supporting Documents */}
                <section className={styles.card}>
                    <div className={styles.cardHeader}>
                        <FaFileAlt className={styles.cardIcon} />
                        <h2>Supporting Documents</h2>
                    </div>
                    <p>
                        Applicants for a <strong>Conference Visa</strong> may be required to submit the following documents:
                    </p>
                    <ul className={styles.styledList}>
                        <li><strong>Invitation Letter</strong> from IVEC 2027</li>
                        <li><strong>Clearance Letter</strong> from Ministry of External Affairs (MEA)</li>
                        <li><strong>Approval Letter</strong> from Ministry of Home Affairs (MHA) <em>(required only for specific nationalities)</em></li>
                        <li>Valid Passport</li>
                        <li>Completed Visa Application Form</li>
                        <li>Passport-size photographs <em>(as per specifications)</em></li>
                    </ul>
                    <p className={styles.mutedFooterText}>
                        These documents will be provided by the <strong>IVEC 2027 Secretariat</strong> where applicable.
                    </p>
                </section>

                {/* Invitation Letter & Accompanying Persons Grid */}
                <div className={styles.twoColumnGrid}>
                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <FaEnvelope className={styles.cardIcon} />
                            <h2>Invitation Letter</h2>
                        </div>
                        <p>
                            The IVEC 2027 Secretariat can provide an <strong>official invitation letter</strong> for visa purposes.
                        </p>
                        <p className={styles.highlightText}>
                            Invitation letters will be issued <strong>only after registration is completed and full payment has been received</strong>.
                        </p>
                        <p>Participants requesting an invitation letter must provide:</p>
                        <ul className={styles.compactList}>
                            <li>Full Name (as per passport)</li>
                            <li>Passport Number</li>
                            <li>Place of Issue & Date of Issue</li>
                            <li>Nationality</li>
                            <li>Affiliation (City and Country)</li>
                        </ul>
                        <p className={styles.warningNote}>
                            The information must <strong>exactly match the passport details</strong>. Any discrepancy may result in visa delays or rejection.
                        </p>
                        <p className={styles.disclaimerSmall}>
                            *Please note that an invitation letter <strong>does not guarantee visa approval</strong>.
                        </p>
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <FaUserFriends className={styles.cardIcon} />
                            <h2>Accompanying Persons</h2>
                        </div>
                        <p>
                            Accompanying persons who are <strong>not attending the conference sessions</strong> should apply for a <strong>Tourist Visa</strong>.
                        </p>
                        <p>They may participate in:</p>
                        <ul className={styles.compactList}>
                            <li>Social events</li>
                            <li>Networking activities</li>
                        </ul>
                        <p>Tourist visas can often be obtained through the <strong>e-Tourist Visa</strong> facility:</p>
                        {/* e-Tourist Visa Portal Link */}
                        <a
                            href="https://indianvisaonline.gov.in/evisa/tvoa.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.externalLinkBtn}
                        >
                            <span>Visit e-Visa Portal</span>
                            <FaExternalLinkAlt className={styles.linkIcon} />
                        </a>
                    </section>
                </div>

                {/* Visa Processing Time & Important Notes Grid */}
                <div className={styles.twoColumnGrid}>
                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <FaClock className={styles.cardIcon} />
                            <h2>Visa Processing Time</h2>
                        </div>
                        <p>Typical visa processing times:</p>
                        <div className={styles.timeBox}>
                            <p>
                                <strong>Most countries:</strong> approximately 14 days
                            </p>
                            <p>
                                <strong>Afghanistan, Iraq, Pakistan, Sudan, China, and Stateless persons:</strong> up to 60 days
                            </p>
                        </div>
                        <p className={styles.urgentNotice}>
                            Participants are strongly advised to <strong>apply at least 6–8 weeks before travel</strong>.
                        </p>
                    </section>

                    <section className={styles.card}>
                        <div className={styles.cardHeader}>
                            <FaExclamationTriangle className={styles.cardIcon} />
                            <h2>Important Notes</h2>
                        </div>
                        <p>
                            The IVEC 2027 Organizing Committee <strong>can not influence visa decisions</strong> made by the Government of India or any embassy/consulate.
                        </p>
                        <p>The organizers can only provide:</p>
                        <ul className={styles.compactList}>
                            <li>Invitation Letter</li>
                            <li>Conference documentation required for visa applications</li>
                        </ul>
                        <p className={styles.disclaimerSmall}>
                            Visa approval remains <strong>solely at the discretion of the Indian Embassy or Consulate</strong>. Conference registration fees <strong>can not be refunded due to visa rejection</strong>. The conference cancellation policy will apply.
                        </p>
                    </section>
                </div>

                {/* Enquiries Card */}
                <section className={`${styles.card} ${styles.contactCard}`}>
                    <div className={styles.cardHeader}>
                        <FaEnvelope className={styles.cardIcon} />
                        <h2>Enquiries</h2>
                    </div>
                    <p>For visa-related queries, please contact the conference secretariat:</p>
                    <div className={styles.emailContainer}>
                        <span className={styles.emailLabel}>Email:</span>

                        {/* 1. Added mailto: to launch mail app directly */}
                        <a href={`mailto:${email}`} className={styles.emailLink}>
                            {email}
                        </a>

                        {/* 2. Added Copy Button for smooth UX */}
                        <button
                            type="button"
                            onClick={handleCopy}
                            className={styles.copyButton}
                            title="Copy email address"
                        >
                            {copied ? (
                                <span className={styles.copiedBadge}><FaCheck /> Copied!</span>
                            ) : (
                                <FaRegCopy />
                            )}
                        </button>
                    </div>
                    <p style={{ marginTop: "1rem" }}>
                        Please include the following information when contacting the secretariat:
                    </p>
                    <ul className={styles.bulletListInline}>
                        <li>Registration confirmation</li>
                        <li>Full name (as per passport)</li>
                        <li>Nationality</li>
                        <li>Passport details</li>
                    </ul>
                </section>

                {/* Disclaimer */}
                <section className={`${styles.card} ${styles.disclaimerCard}`}>
                    <div className={styles.cardHeader}>
                        <FaShieldAlt className={styles.cardIcon} />
                        <h2>Disclaimer</h2>
                    </div>
                    <p>
                        Participants should verify entry and visa requirements <strong>at least three months before travel</strong>, as regulations may vary by country.
                    </p>
                    <p>The <strong>IVEC 2027</strong> organizers and technical sponsors:</p>
                    <ul className={styles.styledList}>
                        <li>Do not issue sponsorship or guarantee letters</li>
                        <li>Cannot intervene in visa decisions</li>
                        <li>Cannot appeal visa rejections on behalf of participants</li>
                    </ul>
                    <p className={styles.disclaimerFooter}>
                        For the most accurate information, please contact the <strong>Indian Embassy or Consulate</strong> in your country.
                    </p>
                </section>

                {/* Frequently Asked Questions */}
                <section className={styles.card}>
                    <div className={styles.cardHeader}>
                        <FaInfoCircle className={styles.cardIcon} />
                        <h2>Frequently Asked Questions (FAQ)</h2>
                    </div>
                    <div className={styles.accordion}>
                        {faqs.map((faq, idx) => (
                            <div
                                key={idx}
                                className={`${styles.faqItem} ${openFaq === idx ? styles.faqOpen : ""
                                    }`}
                            >
                                <button
                                    className={styles.faqQuestion}
                                    onClick={() => toggleFaq(idx)}
                                >
                                    <span>{faq.q}</span>
                                    <FaChevronDown className={styles.faqIcon} />
                                </button>
                                {openFaq === idx && (
                                    <div className={styles.faqAnswer}>
                                        <p>{faq.a}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className={styles.faqFooterLink}>
                        <p>For further information, visit:</p>
                        <a
                            href="https://conference.mha.gov.in/events/FAQ.aspx"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.externalLinkBtn}
                        >
                            <span>Read Official MHA Visa FAQs</span>
                            <FaExternalLinkAlt className={styles.linkIcon} />
                        </a>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default VisaHelpPage;